<template>
  <div class="docs-container">
    <div class="docs-wrapper">
      <Card class="docs-card">
        <template #content>
          <div v-if="loading" class="loading">
            <i class="pi pi-spinner pi-spin"></i> Loading documentation...
          </div>
          <div v-else-if="error" class="error">
            <i class="pi pi-exclamation-triangle"></i> {{ error }}
          </div>
          <div v-else class="docs-content" v-html="htmlContent"></div>
        </template>
      </Card>
    </div>
    
    <!-- Table of Contents Button -->
    <Button 
      v-if="!loading && !error"
      @click="scrollToContents"
      icon="pi pi-list" 
      class="toc-button"
      rounded
      size="large"
      v-tooltip.left="'Jump to Contents'"
      severity="secondary"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { marked } from 'marked'
import Card from 'primevue/card'
import Button from 'primevue/button'

const htmlContent = ref('')
const loading = ref(true)
const error = ref('')

// Configure marked options for GitHub-like rendering
marked.setOptions({
  breaks: true,
  gfm: true
})

const scrollToContents = () => {
  const contentsElement = document.querySelector('h2') // "Contents" is typically the first h2
  if (contentsElement) {
    contentsElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } else {
    // Fallback: scroll to top
    document.querySelector('.docs-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const loadUserGuide = async () => {
  try {
    loading.value = true
    const response = await fetch('/static/SPA/docs/user-guide.md')
    if (!response.ok) {
      throw new Error(`Failed to load user guide: ${response.status}`)
    }
    const markdown = await response.text()
    htmlContent.value = await marked(markdown)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load documentation'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadUserGuide()
})
</script>

<style scoped>
.docs-container {
  width: 100%;
  height: calc(100vh - 60px);
  overflow-y: auto;
  padding: 2rem;
  background: var(--p-surface-50);
  position: relative;
}

.docs-wrapper {
  max-width: 1120px;
  margin: 0 auto;
}

.docs-card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  border: 1px solid var(--p-surface-200);
}

.toc-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.toc-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.loading, .error {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  font-size: 1.2rem;
  gap: 0.5rem;
}

.error {
  color: var(--p-red-500);
}

.docs-content {
  line-height: 1.7;
  font-size: 1rem;
  color: #24292f;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .docs-container {
    padding: 1rem;
  }
  
  .docs-content {
    font-size: 0.9rem;
  }
  
  .toc-button {
    bottom: 1rem;
    right: 1rem;
  }
}
</style>

<style>
/* GitHub-like markdown styling */
.docs-content h1 {
  font-size: 2rem;
  font-weight: 600;
  margin: 2rem 0 1rem 0;
  color: #1f2328;
  border-bottom: 1px solid #d1d9e0;
  padding-bottom: 0.3rem;
}

.docs-content h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.5rem 0 1rem 0;
  color: #1f2328;
  border-bottom: 1px solid #d1d9e0;
  padding-bottom: 0.3rem;
}

.docs-content h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1.5rem 0 0.5rem 0;
  color: #1f2328;
}

.docs-content h4 {
  font-size: 1rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
  color: #1f2328;
}

.docs-content p {
  margin: 1rem 0;
  line-height: 1.6;
  color: #656d76;
}

.docs-content ul, .docs-content ol {
  margin: 1rem 0;
  padding-left: 2rem;
  color: #656d76;
}

.docs-content li {
  margin: 0.25rem 0;
  line-height: 1.6;
}

.docs-content li p {
  margin: 0.25rem 0;
}

.docs-content strong {
  font-weight: 600;
  color: #1f2328;
}

.docs-content em {
  font-style: italic;
}

.docs-content a {
  color: #0969da;
  text-decoration: none;
}

.docs-content a:hover {
  text-decoration: underline;
}

.docs-content hr {
  border: none;
  border-top: 1px solid #d1d9e0;
  margin: 2rem 0;
}

.docs-content blockquote {
  border-left: 4px solid #d1d9e0;
  padding-left: 1rem;
  margin: 1rem 0;
  background: #f6f8fa;
  padding: 1rem;
  border-radius: 6px;
  color: #656d76;
}

.docs-content code {
  background: rgba(175, 184, 193, 0.2);
  padding: 0.2rem 0.4rem;
  border-radius: 6px;
  font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', monospace;
  font-size: 0.85em;
  color: #1f2328;
}

.docs-content pre {
  background: #f6f8fa;
  border: 1px solid #d1d9e0;
  border-radius: 6px;
  padding: 1rem;
  overflow-x: auto;
  margin: 1rem 0;
  color: #1f2328;
}

.docs-content pre code {
  background: none;
  padding: 0;
  border-radius: 0;
  border: none;
}

.docs-content table {
  border-collapse: collapse;
  margin: 1rem 0;
  width: 100%;
}

.docs-content th, .docs-content td {
  border: 1px solid #d1d9e0;
  padding: 0.5rem 1rem;
  text-align: left;
}

.docs-content th {
  background: #f6f8fa;
  font-weight: 600;
}

/* GitHub-like task lists */
.docs-content input[type="checkbox"] {
  margin-right: 0.5rem;
}

/* Link styling for internal anchors */
.docs-content h1:hover::before,
.docs-content h2:hover::before,
.docs-content h3:hover::before,
.docs-content h4:hover::before {
  content: '🔗';
  margin-left: -1.5rem;
  margin-right: 0.5rem;
  opacity: 0.6;
  cursor: pointer;
}
</style>
